import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

const CATEGORIES = {
  all: () => true,
  metrics: (a) => a.action.toLowerCase().includes("metric"),
  invoices: (a) => a.action.toLowerCase().includes("invoice"),
  team: (a) => a.action.toLowerCase().includes("team") || a.action.toLowerCase().includes("client"),
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("activity_log")
      .select("*, users(full_name, email), clients(company_name)")
      .order("created_at", { ascending: false })
      .limit(100);
    setEntries(data || []);
    setLoading(false);
  }

  const filtered = useMemo(() => {
    return entries
      .filter(CATEGORIES[filter])
      .filter((e) => {
        if (!search.trim()) return true;
        const haystack = `${e.action} ${e.details || ""} ${e.clients?.company_name || ""}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      });
  }, [entries, filter, search]);

  function tagFor(action) {
    const a = action.toLowerCase();
    if (a.includes("metric")) return { label: "metric", cls: "bg-accent text-foreground" };
    if (a.includes("invoice")) return { label: "invoice", cls: "bg-destructive/10 text-destructive" };
    if (a.includes("team") || a.includes("client")) return { label: "team", cls: "bg-accent text-foreground" };
    return { label: "activity", cls: "bg-accent text-foreground" };
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Audit log</h1>
      <p className="text-sm text-muted-foreground mb-5">Who changed what, and when</p>

      <div className="flex items-center justify-between mb-4 gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by client or action"
          className="border border-input rounded-lg px-3 py-2 text-sm bg-background text-foreground w-64"
        />
        <div className="flex gap-2">
          {Object.keys(CATEGORIES).map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`font-mono text-[11.5px] px-3 py-1.5 rounded-full border ${
                filter === c ? "bg-primary text-primary-foreground border-primary" : "border-input text-muted-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        {loading ? (
          <p className="text-sm text-muted-foreground p-5">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground p-5">Nothing logged yet.</p>
        ) : (
          filtered.map((e) => {
            const tag = tagFor(e.action);
            return (
              <div key={e.id} className="flex items-start gap-3 px-5 py-3.5 border-b border-border last:border-0">
                <div className="w-7 h-7 rounded-full bg-accent text-foreground flex items-center justify-center text-[10.5px] font-semibold shrink-0">
                  {(e.users?.full_name || e.users?.email || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="text-sm text-foreground">
                    <strong>{e.users?.full_name || e.users?.email || "Someone"}</strong>{" "}
                    {e.action.toLowerCase()}
                    {e.clients?.company_name && (
                      <>
                        {" "}
                        for <strong>{e.clients.company_name}</strong>
                      </>
                    )}
                    {e.details && <span className="text-muted-foreground">, {e.details}</span>}
                    <span className={`font-mono text-[10px] rounded-full px-2 py-0.5 ml-2 ${tag.cls}`}>{tag.label}</span>
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
